import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export default function Ethos() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      gsap.from(".ethos-word", {
        yPercent: 100,
        opacity: 0,
        duration: 1,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: {
          trigger: root.current,
          start: "top 70%",
        },
      });

      document.querySelectorAll<HTMLElement>(".stat-num").forEach((el) => {
        const target = parseFloat(el.dataset.value || "0");
        const suffix = el.dataset.suffix || "";
        const decimals = parseInt(el.dataset.decimals || "0", 10);
        const obj = { val: 0 };
        gsap.to(obj, {
          val: target,
          duration: 2,
          ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 80%" },
          onUpdate: () => {
            el.textContent = obj.val.toFixed(decimals) + suffix;
          },
        });
      });

      gsap.from(".ethos-img-clip", {
        clipPath: "inset(100% 0 0 0)",
        duration: 1.4,
        ease: "power4.out",
        scrollTrigger: { trigger: ".ethos-img", start: "top 75%" },
      });
    }, root);

    return () => {
      ctx.revert();
      ScrollTrigger.refresh();
    };
  }, []);

  const headlineLine1 = "We build".split(" ");
  const headlineLine2 = "wardrobes that".split(" ");
  const headlineLine3 = "outlive trends.".split(" ");

  return (
    <section ref={root} className="bg-bone py-10 md:py-14 px-6 md:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
        {/* Image — shown second on mobile, right side on desktop */}
        <div className="lg:col-span-5 ethos-img order-2 lg:order-2">
          <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden bg-sand">
            <div className="ethos-img-clip absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=close%20up%20portrait%20of%20artisan%20woman%20from%20shoulders%20up%20in%20warm%20natural%20light%20atelier%2C%20calm%20expression%20focused%20eyes%2C%20cream%20linen%20fabric%20nearby%2C%20sustainable%20fashion%20studio%20documentary%20photography%2C%20rich%20texture%20detail%2C%20earth%20tone%20palette%2C%20artisanal%20slow%20craft%20editorial%2C%20intimate%20warm%20mood%20with%20soft%20golden%20light&width=900&height=1100&seq=verdant-ethos-2&orientation=portrait"
                alt="Artisan at work"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-ink/70 via-ink/20 to-transparent">
              <p className="text-bone text-sm font-light">Mara, pattern cutter</p>
              <p className="text-bone/70 text-xs mt-1">9 years with the studio</p>
            </div>
          </div>
        </div>

        {/* Text — shown first on mobile, left side on desktop */}
        <div className="lg:col-span-7 order-1 lg:order-1">
          <p className="text-xs font-mono uppercase tracking-[0.22em] text-ink/50 mb-4">
            Our Philosophy
          </p>
          <h2 className="font-display tracking-tightest leading-[0.95] text-ink text-4xl md:text-5xl lg:text-6xl font-light">
            {[headlineLine1, headlineLine2, headlineLine3].map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <span className="inline-block">
                  {line.map((word, j) => (
                    <span key={`${i}-${j}`} className="inline-block overflow-hidden">
                      <span
                        className={`ethos-word inline-block ${
                          i === 2 && j === 0 ? "font-display text-rust" : ""
                        }`}
                      >
                        {word}&nbsp;
                      </span>
                    </span>
                  ))}
                </span>
              </span>
            ))}
          </h2>
          <p className="mt-4 text-ink/70 text-base leading-relaxed max-w-xl font-light">
            Every piece we release is designed to be repaired, not replaced. We source fibers from regenerative farms, cut patterns to eliminate waste, and price garments to reflect true labor value. There are no seasonal drops. When a fabric runs out, the style retires.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-8">
            <div>
              <p className="font-display text-3xl md:text-4xl text-ink tracking-tightest font-light">
                <span className="stat-num" data-value="12" data-suffix="" data-decimals="0">0</span>
              </p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-ink/50 font-mono">Styles per year</p>
            </div>
            <div>
              <p className="font-display text-3xl md:text-4xl text-ink tracking-tightest font-light">
                <span className="stat-num" data-value="8" data-suffix=" yrs" data-decimals="0">0 yrs</span>
              </p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-ink/50 font-mono">Average lifespan</p>
            </div>
            <div>
              <p className="font-display text-3xl md:text-4xl text-ink tracking-tightest font-light">
                <span className="stat-num" data-value="100" data-suffix="%" data-decimals="0">0%</span>
              </p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-ink/50 font-mono">Repairable design</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}